import { newGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { AccountSettingsPageContext } from "@insite/content-library/Pages/AccountSettingsPage";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection";

import { createSession, Session } from "@insite/client-framework/Services/SessionService";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomerPartNumberModel } from "@tCooneyBrothers/ApiModels";
import addCustomerPartNumber from "@tCooneyBrothers/Handlers/AddCustomerPartNumber";
import deleteCustomerPartNumber, { DeleteCpnParameter } from "@tCooneyBrothers/Handlers/DeleteCustomerPartNumber";
import loadCustomerPartNumbers, { AddCpnParameter } from "@tCooneyBrothers/Handlers/LoadCustomerPartNumbers";
import CustomApplicationState from "@tCooneyBrothers/Store/CustomApplicationState";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    customerPartNumberModel?: CustomerPartNumberModel[];
}

const mapDispatchToProps = {
    loadCustomerPartNumbers,
    addCustomerPartNumber,
    deleteCustomerPartNumber,
    setAddToListModalIsOpen,
};

const mapStateToProps = (state: CustomApplicationState) => ({
    selectedProductId: state.pages?.productDetails?.selectedProductId,
    selectedCustomerId: state.context?.session?.billToId,
    productCpns: state.cbData?.customerPartNumber?.value,
    session: state.context.session,
});

type Props = OwnProps & HasProduct & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

const typographyProps = {
    as: "div",
    style: {
        width: "70%",
    },
};

export interface AddRemoveCustomerPartNumberStyleCustoms {
    CustomerPartNumberTitle: TypographyProps;
    CustomerPartNumberRow?: InjectableCss;
    userInputText?: InjectableCss;
    addButton?: ButtonPresentationProps;
    horizontalLine?: InjectableCss;
    removeButton?: InjectableCss;
    productName?: ButtonPresentationProps;
    scroller?: InjectableCss;
    removeLink?: InjectableCss;
}

const styles: AddRemoveCustomerPartNumberStyleCustoms = {
    CustomerPartNumberTitle: {
        css: css`
            [class^="AccordionStyle"] {
                margin-top: 0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                width: 100%;
                            `,
                            css`
                                width: 100%;
                            `,
                            null,
                        ],
                        "min",
                    )}
            }

            [data-test-selector="sectionHeader"] {
                > button {
                    width: 100%;
                    text-transform: uppercase;
                    transition: all 200ms ease-in-out 0s;
                    padding: 0px;
                    border: 2px solid #2450a8;
                    background: #fff;
                    height: 40px;
                    justify-content: center;
                    margin-top: 10px;
                    color: #2450a8;
                    [class^="IconWrapper"] {
                        display: none;
                    }
                    span {
                        font-weight: 600;
                        font-size: 14px;
                        color: inherit;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    null,
                                    null,
                                    css`
                                        font-size: 11px;
                                    `,
                                    null,
                                    null,
                                ],
                                "max",
                            )}
                    }
                    &:hover {
                        border: 2px solid #000066;
                        color: #000066;
                    }
                }
            }
            [data-test-selector="sectionPanel"] {
                padding: 10px 5px !important;
            }
        `,
    },
    CustomerPartNumberRow: {
        css: css`
            display: flex;
            flex-direction: row;
            div[class^="FormFieldStyle"] {
                width: 70%;
                input {
                    height: 40px;
                    ::placeholder {
                        font-size: 11px;
                        opacity: 1; /* Firefox */
                    }
                    :-ms-input-placeholder {
                        /* Internet Explorer 10-11 */
                        font-size: 11px;
                    }
                    ::-ms-input-placeholder {
                        /* Microsoft Edge */
                        font-size: 11px;
                    }
                }
            }
            button[class^="ButtonWrapper"] {
                width: 45%;
                height: 33px;
                span {
                    font-weight: normal;
                    font-size: 14px;
                }
            }
        `,
    },
    userInputText: {
        css: css`
            display: flex;
        `,
    },
    productName: {
        css: css`
            width: 70%;
            color: #353432;
            text-decoration: bold;
        `,
    },
    addButton: {
        css: css`
            background: #59b46c;
            display: flex;
            pointer-events: none;
            & span {
                margin-top: 8px;
            }
        `,
    },
    horizontalLine: {
        css: css`
            height: 1px;
            border: none;
            background-color: lightgray;
        `,
    },
    scroller: {
        css: css`
            max-height: 70px;
            overflow: auto;
            div.collection-item {
                display: flex;
                align-items: center;
                margin-bottom: 5px;
                max-width: 100%;
                > span {
                    width: 70%;
                    background: #f8f8f8;
                    height: 100%;
                    display: block;
                    padding: 7px 10px;
                }
                button {
                    padding: 0;
                    width: 42%;
                    height: 33px;
                    span {
                        font-size: 13px !important;
                        font-weight: normal !important;
                    }
                    > span[class^="TypographyStyle"] {
                        color: #ef3742 !important;
                        font-weight: 600 !important;
                    }
                }
            }
        `,
    },
    removeButton: {
        css: css`
            display: inline-block;
            color: #ff0000;
            float: right;
            margin-right: 30px;
            & Link {
                color: red !important;
            }
        `,
    },
    removeLink: {
        css: css`
            justify-content: center;
        `,
    },
};

export const CustomerPartNumberStyles = styles;
const StyledSpan = getStyledWrapper("span");
const StyledHr = getStyledWrapper("hr");
const StyledDiv = getStyledWrapper("div");

const AddRemoveCustomerPartNumberCustom: FC<Props> = ({
    customerPartNumberModel,
    loadCustomerPartNumbers,
    addCustomerPartNumber,
    selectedProductId,
    selectedCustomerId,
    deleteCustomerPartNumber,
    productCpns,
    session,
    setAddToListModalIsOpen,
    product,
}) => {
    const [newCPN, setnewCPN] = React.useState("");

    const newCPNChangeHandler = (input: HTMLInputElement) => {
        setnewCPN(input.value);
    };
    const allAccordianState = {
        company: false,
    };

    useEffect(() => {
        if (selectedProductId) {
            const getCPN: AddCpnParameter = {
                productId: selectedProductId,
            };
            loadCustomerPartNumbers(getCPN);
        }
    }, [selectedProductId]);

    const [accordianState, setAccordianState] = React.useState(allAccordianState);

    const ProductCpn = (pCpn: CustomerPartNumberModel) => {
        return (
            <div className="collection-item" key={pCpn.id}>
                <span>{pCpn.name}</span>
                <Link {...styles.removeLink} onClick={() => deleteCPNHandler(pCpn)}>
                    Remove
                </Link>
            </div>
        );
    };
    const deleteCPNHandler = (pcpn: CustomerPartNumberModel) => {
        if (pcpn && selectedProductId) {
            const alkadelcpn: DeleteCpnParameter = {
                id: pcpn.id,
                productId: pcpn.productId,
            };
            deleteCustomerPartNumber(alkadelcpn);
            if (selectedProductId) {
                const getCPN: AddCpnParameter = {
                    productId: selectedProductId,
                };
                loadCustomerPartNumbers(getCPN);
            }
        }
    };

    const addCPNHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (selectedCustomerId && selectedProductId && newCPN.trim()) {
            const addCpn: CustomerPartNumberModel = {
                id: newGuid(),
                customerId: selectedCustomerId,
                productId: selectedProductId,
                name: newCPN,
            };
            addCustomerPartNumber(addCpn);
            if (selectedProductId) {
                const getCPN: AddCpnParameter = {
                    productId: selectedProductId,
                };
                loadCustomerPartNumbers(getCPN);
            }
        }
        setnewCPN("");
    };

    // eslint-disable-next-line require-await
    const handleAccordianState = async (accordianName: string) => {
        const newAccoridan = { ...accordianState };
        for (const key in newAccoridan) {
            if (session.isAuthenticated === false) {
                (newAccoridan as any)[key] = false;
                setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [] });
            } else if (session.isAuthenticated === true) {
                (newAccoridan as any)[key] = !(newAccoridan as any)[key];
            }
        }
        setAccordianState(newAccoridan);
        if (selectedProductId) {
            const getCPN: AddCpnParameter = {
                productId: selectedProductId,
            };
            loadCustomerPartNumbers(getCPN);
        }
    };

    const showDiscontinuedTag = product.properties?.discontinuedProduct?.toLowerCase() === "true" || false;
    if (showDiscontinuedTag) {
        return null;
    }
    const donotDisplayPrice = product.properties?.donotDisplayPrice?.toLowerCase() === "true" || false;
    if (donotDisplayPrice) {
        return null;
    }
    return (
        <StyledWrapper {...styles.CustomerPartNumberTitle}>
            <Accordion headingLevel={2}>
                <AccordionSection
                    expanded={accordianState.company}
                    title="+/- CUSTOMER PART NUMBER"
                    onTogglePanel={status => {
                        handleAccordianState("company");
                    }}
                >
                    <StyledDiv>
                        <StyledWrapper {...styles.CustomerPartNumberRow}>
                            <TextField
                                placeholder="Enter New CPN"
                                value={newCPN}
                                onChange={e => newCPNChangeHandler(e.currentTarget)}
                                name={newCPN}
                            />
                            <Button variant="tertiary" onClick={addCPNHandler}>
                                Add
                            </Button>
                        </StyledWrapper>
                        {productCpns ? productCpns.length > 0 ? <StyledHr {...styles.horizontalLine} /> : null : null}
                        <StyledDiv {...styles.scroller}>
                            {productCpns?.map((aCpn: CustomerPartNumberModel) => (
                                <>
                                    <ProductCpn {...aCpn} />
                                </>
                            ))}
                        </StyledDiv>
                    </StyledDiv>
                </AccordionSection>
            </Accordion>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withProduct(AddRemoveCustomerPartNumberCustom)),
    definition: {
        group: "Product Details",
        displayName: "Add Remove Customer Part Number",
    },
};

export default widgetModule;
